import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption } from '@heidi/design-system/typography';
import { type ModalId, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type {
  ComplimentaryTrialEndAnalyticsContext,
  ComplimentaryTrialEndBannerAction,
} from '../../../contracts/complimentary-trial-end-analytics.types';
import { complimentaryTrialEndDevOverrideAtom } from '../../../data/billing-dev/complimentary-trial-end-overrides';
import { COMPLIMENTARY_TRIAL_END_BANNER_MODAL_ID } from '../../../data/complimentary-trial-end-banner-modal-id';
import { isComplimentaryTrialEndBannerDismissed } from '../../../data/query/complimentary-trial-end.utils';
import { useQueriedComplimentaryTrialEndState } from '../../settings/use-complimentary-trial-end-state';
import { formatCreditAllowanceCycleEndDate } from '../credit-usage-popover/format-credit-allowance-reset-label';
import { complimentaryTrialEndModalEntryPointAtom } from './complimentary-trial-end-atoms';
import { complimentaryTrialEndBannerMessages as messages } from './complimentary-trial-end.messages';

/**
 * App inputs, supplied BY REFERENCE at the layout so the allowance read and the
 * flag exposures run only where the banner mounts.
 */
export type ComplimentaryTrialEndBannerPorts = {
  useIsHeidi2OptInEnabled: () => boolean;
  useIsHeidi2TrialEndEnabled: () => boolean;
  useReportTopStrip: (isShowing: boolean) => void;
  /** Claims the shared top-strip slot; true only while this strip wins. */
  useClaimTopStrip: (isEligible: boolean) => boolean;
  useIsClinicalRecordingActive: () => boolean;
  /** Returns the opener for Home with the Tasks list expanded. */
  useOpenHomeTasks: () => () => void;
  /** Returns the `seen_modals` writer; it updates the cached user straight away. */
  useMarkModalAsViewed: () => (view: {
    modal_id: ModalId;
    increment_count: boolean;
    action: ModalViewAction;
  }) => void;
  isAppleCompliantBuild: () => boolean;
  getUsageLimitsHelpUrl: (locale?: string) => string;
  trackComplimentaryTrialEndBannerViewed: (
    context: ComplimentaryTrialEndAnalyticsContext
  ) => void;
  trackComplimentaryTrialEndBannerClick: (
    context: ComplimentaryTrialEndAnalyticsContext & {
      action: ComplimentaryTrialEndBannerAction;
      label: string;
    }
  ) => void;
};

/**
 * Full-width strip above the navigation for the last week of complimentary Max
 * usage. Active members are sent to the T-7 upgrade modal; inactive members are
 * nudged to try an agent task first. Stays until dismissed or its CTA is used,
 * once per complimentary cycle, and steps aside during a clinical recording.
 */
export function ComplimentaryTrialEndBanner({
  ports,
}: {
  ports: ComplimentaryTrialEndBannerPorts;
}) {
  const intl = useIntl();
  const trialEnd = useQueriedComplimentaryTrialEndState({
    isHeidi2OptInEnabled: ports.useIsHeidi2OptInEnabled(),
    isTrialEndEnabled: ports.useIsHeidi2TrialEndEnabled(),
  });
  const { data: bannerLastSeenAt } = useQueryUser(
    (user) => user?.seen_modals?.[COMPLIMENTARY_TRIAL_END_BANNER_MODAL_ID]?.last_seen_at
  );
  const devResetAt = useAtomValue(
    complimentaryTrialEndDevOverrideAtom
  )?.bannerDismissalResetAt;
  const markModalAsViewed = ports.useMarkModalAsViewed();
  const setModalEntryPoint = useSetAtom(complimentaryTrialEndModalEntryPointAtom);
  const isClinicalRecordingActive = ports.useIsClinicalRecordingActive();
  const openHomeTasks = ports.useOpenHomeTasks();

  // The active variant only sells an upgrade, so Apple builds never see it.
  const isActiveCohort = trialEnd?.cohort === 'active';
  // Claim while the cohort is active even during recording so lower-priority
  // strips stay suppressed and the clinical workspace does not reflow. Paint
  // only when recording is idle.
  const isActive =
    trialEnd != null &&
    !isComplimentaryTrialEndBannerDismissed({
      lastSeenAt: bannerLastSeenAt,
      cycleEnd: trialEnd.cycleEnd,
      notBefore: devResetAt,
    }) &&
    !(isActiveCohort && ports.isAppleCompliantBuild());
  const holdsSlot = ports.useClaimTopStrip(isActive);
  const isVisible = holdsSlot && !isClinicalRecordingActive;
  ports.useReportTopStrip(isVisible);

  const daysLeft = trialEnd?.daysLeft;
  const usagePercent = trialEnd?.usagePercent;
  const cohort = trialEnd?.cohort;
  const cycleEnd = trialEnd?.cycleEnd;
  const analyticsContext = useMemo<ComplimentaryTrialEndAnalyticsContext | null>(
    () =>
      daysLeft == null || usagePercent == null || cohort == null
        ? null
        : { daysRemaining: daysLeft, usagePercent, cohort },
    [cohort, daysLeft, usagePercent]
  );
  const viewTrackedRef = useRef(false);
  const {
    trackComplimentaryTrialEndBannerViewed,
    trackComplimentaryTrialEndBannerClick,
  } = ports;
  useEffect(() => {
    if (!isVisible || analyticsContext == null || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackComplimentaryTrialEndBannerViewed(analyticsContext);
  }, [analyticsContext, isVisible, trackComplimentaryTrialEndBannerViewed]);

  const handleAction = useCallback(
    (action: ComplimentaryTrialEndBannerAction, label: string) => {
      if (cycleEnd == null || analyticsContext == null) return;
      trackComplimentaryTrialEndBannerClick({ ...analyticsContext, action, label });
      markModalAsViewed({
        modal_id: COMPLIMENTARY_TRIAL_END_BANNER_MODAL_ID,
        increment_count: false,
        action: action === 'dismiss' ? ModalViewAction.dismiss : ModalViewAction.engage,
      });
      if (action === 'keep_usage') {
        setModalEntryPoint('banner');
      } else if (action === 'give_task') {
        openHomeTasks();
      }
    },
    [
      analyticsContext,
      cycleEnd,
      markModalAsViewed,
      openHomeTasks,
      setModalEntryPoint,
      trackComplimentaryTrialEndBannerClick,
    ]
  );

  if (!isVisible || trialEnd == null) return null;

  const ctaMessage = isActiveCohort ? messages.keepUsage : messages.giveTask;
  const ctaAction: ComplimentaryTrialEndBannerAction = isActiveCohort
    ? 'keep_usage'
    : 'give_task';

  return (
    <section
      className="relative flex min-h-9 w-full shrink-0 items-center justify-center gap-3 bg-tier-pro-muted px-10 py-1.5 text-text-primary"
      data-testid="complimentary-trial-end-banner"
      data-cohort={trialEnd.cohort}
    >
      <TypographyCaption className="text-center">
        <FormattedMessage
          {...(isActiveCohort ? messages.activeBody : messages.inactiveBody)}
          values={{
            date: formatCreditAllowanceCycleEndDate(intl, trialEnd.cycleEnd),
            emphasis: (chunks: ReactNode) => (
              <span className="font-semibold">{chunks}</span>
            ),
            limits: (chunks: ReactNode) => (
              <Link
                variant="inline"
                size="inherit"
                className="text-text-primary decoration-text-primary hover:text-text-primary hover:decoration-text-primary focus-visible:text-text-primary"
                href={ports.getUsageLimitsHelpUrl(intl.locale)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </TypographyCaption>
      <Button
        type="button"
        variant="surface"
        size="xxs"
        onClick={() => handleAction(ctaAction, intl.formatMessage(ctaMessage))}
        data-testid="complimentary-trial-end-banner-cta"
      >
        <FormattedMessage {...ctaMessage} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="xxs"
        iconOnly
        aria-label={intl.formatMessage(messages.dismissLabel)}
        className="absolute end-1.5 text-text-primary"
        onClick={() => handleAction('dismiss', intl.formatMessage(messages.dismissLabel))}
        data-testid="complimentary-trial-end-banner-dismiss"
      >
        <X />
      </Button>
    </section>
  );
}
